<template>
    <div class="meta-nf-post" :class="postExtraClass">
        <div class="header">
            <div class="avatar">
                <Icon type="logo-facebook" />
            </div>
            <div class="user-info">
                <div class="user-name">
                    Business Name
                    <br />
                    <span class="sponsored">
                        Sponsored -
                        <Icon type="md-globe" />
                    </span>
                </div>
            </div>
            <div class="options">⋯</div>
        </div>

        <div class="content">
            <div v-if="metaOverwrites?.fbPrimaryText && adSubType !== AdSubType.Video" class="text">
                {{ metaOverwrites?.fbPrimaryText }}
            </div>

            <div :key="replayKey" class="post_media">
                <slot />

                <div v-if="adSubType === AdSubType.Desktop" class="overlay" :class="overlayExtraClass">
                    <div class="desktop-info">
                        <span class="desktop-play"><Icon type="ios-game-controller-b" /></span>
                        <div>
                            <span>{{ metaOverwrites.fbCallToAction }}</span>
                            <a>http://google.com</a>
                        </div>
                    </div>
                    <Icon type="ios-refresh" class="desktop-refresh" @click="replayBanner" />
                </div>
            </div>

            <div v-if="metaOverwrites?.fbDisplayLink || metaOverwrites.fbDescription" class="description">
                <div class="description-text">
                    <a v-if="metaOverwrites?.fbDisplayLink && adSubType !== AdSubType.Video" :href="safeUrl">
                        {{ metaOverwrites.fbDisplayLink }}
                    </a>

                    <p v-if="metaOverwrites.fbDescription || metaOverwrites.fbHeadline">
                        <strong v-if="metaOverwrites.fbHeadline">{{ metaOverwrites.fbHeadline }}</strong>
                        <br v-if="metaOverwrites.fbDescription" />
                        <span v-if="adSubType !== AdSubType.Video">{{ metaOverwrites?.fbDescription }}</span>
                    </p>

                    <div
                        v-if="metaOverwrites?.fbPrimaryText && adSubType === AdSubType.Video"
                        class="description-text--primary"
                    >
                        {{ metaOverwrites?.fbPrimaryText }}
                    </div>
                </div>

                <button v-if="metaOverwrites?.fbCallToAction" class="description-btn">
                    {{ metaOverwrites.fbCallToAction }}
                </button>
            </div>
        </div>

        <div class="footer">
            <div class="divider"></div>
            <div class="actions">
                <div class="action">
                    <span>
                        <Icon type="ios-thumbs-up-outline" />
                        Like
                    </span>
                </div>
                <div class="action">
                    <span>
                        <Icon type="ios-chatbubbles-outline" />
                        Comment
                    </span>
                </div>
                <div class="action">
                    <span>
                        <Icon type="ios-share-alt-outline" />
                        Share
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { AdSubType } from "@/enums/assemble";
import type { MetaOverwrites } from "@/types/types";
import sanitizeUrl from "@/utils/sanitizeUrl";
import type { MasterTemplate } from "hoxton-types";
import type { PropType } from "vue";

export default {
    name: "FacebookNewsFeed",
    props: {
        metaOverwrites: {
            type: Object as PropType<MetaOverwrites>,
            default: () => ({})
        },

        masterTemplate: {
            type: Object as PropType<MasterTemplate>
        },

        adSubType: {
            type: String as PropType<(typeof AdSubType)[keyof typeof AdSubType]>,
            default: AdSubType.Video
        },

        adSize: {
            type: Number
        }
    },

    data() {
        return {
            AdSubType,
            safeUrl: "",
            replayKey: 0,
            overlayExtraClass: "",
            hideTimeout: null as ReturnType<typeof setTimeout> | null
        };
    },
    computed: {
        postExtraClass() {
            switch (this.adSubType) {
                case AdSubType.Video:
                    return "video-type";
                case AdSubType.Tab:
                    return "tab-type";
                case AdSubType.Desktop:
                    return "desktop-type";
                default:
                    return "";
            }
        }
    },
    watch: {
        adSubType(type) {
            if (type === AdSubType.Desktop) {
                if (this.hideTimeout) {
                    clearTimeout(this.hideTimeout);
                }
                this.overlayExtraClass = "hidden";
                this.hideTimeout = setTimeout(() => {
                    this.overlayExtraClass = "";
                }, 10000);
            }
        }
    },
    mounted() {
        if (this.metaOverwrites.fbWebsiteUrl) {
            this.safeUrl = this.sanitizeUrl(this.metaOverwrites.fbWebsiteUrl);
        }
    },
    beforeUnmount() {
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
        }
    },
    methods: {
        replayBanner() {
            this.replayKey += 1;
            this.overlayExtraClass = "hidden";
            this.hideTimeout = setTimeout(() => {
                this.overlayExtraClass = "";
            }, 10000);
        },
        sanitizeUrl
    }
};
</script>

<style lang="scss" scoped>
/* layout */
.meta-nf-post {
    flex: 1;

    display: flex;
    flex-direction: column;

    .header,
    .footer {
        padding: 12px 16px;
    }

    .footer {
        flex: 0 0 78px;
    }

    .header {
        flex: 0 0 66px;
        display: flex;
        align-items: center;

        .user-info {
            flex-grow: 1;
        }
    }

    .content {
        flex: 1;
        display: flex;
        flex-direction: column;

        .post_media {
            flex: 1;
            display: flex;
            overflow: hidden;
        }
    }

    .description {
        display: flex;
        padding: 10px 16px;
    }

    .divider {
        height: 1px;
        margin: 8px 0;
    }

    .video-type {
        .description {
            flex-direction: column;
            border-bottom: 0;
        }
    }
}

/* style */
.meta-nf-post {
    background-color: #fff;

    border-top: 1px solid #ccd0d5;
    border-bottom: 1px solid #ccd0d5;

    .header {
        .avatar {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            margin-right: 10px;

            i {
                width: 100%;
                height: 100%;
                font-size: 42px;
            }
        }

        .user-info {
            .user-name {
                font-weight: bold;
                font-size: 14px;
            }

            .sponsored {
                font-weight: normal;
                font-size: 12px;
                color: #606770;
                i {
                    font-size: 14px;
                }
            }

            .time {
                color: #606770;
                font-size: 12px;
            }
        }

        .options {
            font-size: 24px;
            color: #606770;
            cursor: pointer;
        }
    }

    .content {
        .text {
            font-size: 16px;
            padding: 0 16px;
            margin-bottom: 15px;
            color: #1c1e21;
        }

        .image {
            width: 100%;
            border-radius: 8px;
        }
    }

    .description {
        background: #f7f8fa;
        border-bottom: 1px solid #e4e5e6;
        color: #000;

        .description-text {
            padding-right: 10px;

            a {
                display: inline-block;
                color: #000 !important;
            }
        }

        .description-btn {
            background: #e3e5e9;
            color: #000;
            margin: auto 0 auto auto;
            flex: 0 0 auto;
            border-radius: 4px;
            font-weight: 600;
            border: 0;
            padding: 5px 15px;
        }
    }

    .footer {
        .divider {
            background-color: #dddfe2;
        }

        .actions {
            display: flex;
            justify-content: space-around;
            color: #606770;
            font-size: 14px;

            .action {
                display: flex;
                align-items: center;
                cursor: pointer;
                padding: 8px;
                flex-grow: 1;
                justify-content: center;

                &:hover {
                    background-color: #f0f2f5;
                    border-radius: 4px;
                }

                span {
                    display: flex;
                    color: #000;
                    align-items: center;
                }

                .action-icon {
                    margin-right: 6px;
                    width: 20px;
                    height: 20px;
                }
            }
        }
    }

    .video-type {
        background: #373737;
        color: #fff;

        .description-btn {
            background: #141415;
            width: 100%;
            color: #fff;
            margin: 10px 0;
        }

        .description {
            color: #fff;
            background: #333;
        }

        .header {
            .options {
                color: #fff;
            }

            .user-info {
                .sponsored {
                    color: #fff;
                }
            }
        }

        .footer {
            .actions {
                span {
                    color: #fff;
                }

                .action {
                    color: #fff;
                    padding: 0 8px;
                }
            }
        }
    }

    .tab-type {
        .description-btn,
        .description {
            background: #e9eaee;
        }

        .description-btn {
            color: #000;
            border: 1px solid #000;
            text-transform: uppercase;
            font-weight: 400;
            font-size: 12px;
        }
    }

    .desktop-type {
        .divider {
            background: none;
        }

        .overlay {
            background: rgba(0, 0, 0, 0.75);
            width: 100%;
            height: 100%;
            position: absolute;
            left: 0;
            top: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #fff;

            &.hidden {
                display: none;
            }
        }

        .desktop {
            .desktop-info {
                display: flex;

                > div {
                    display: flex;
                    flex-direction: column;
                    span {
                        font-weight: 600;
                    }
                    a {
                        color: #fff;
                    }
                }
            }

            .desktop-play {
                padding: 5px;
                border-radius: 50%;
                border: 2px solid #fff;
                margin-right: 10px;
                i {
                    font-size: 26px;
                    rotate: -30deg;
                }
            }

            .desktop-refresh {
                position: absolute;
                bottom: 10px;
                left: 10px;
                font-size: 28px;
                font-weight: 600;
                cursor: pointer;
            }
        }

        .description-btn {
            color: #fff;
            background: #0766ff;
        }
    }
}
</style>
